import { PostHog, usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useMemo } from 'react';
import { LineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { MediaPlan } from 'shared/src/domain/media-buy/media-buy-types';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { useLineItemContext } from '@/features/line-items/line-items-context';

type BravoEntity = 'account' | 'campaign' | 'line_item' | 'media_plan' | 'platform_buy';

type DetailPageViewedEvent = {
  name: 'detail_page_viewed';
  entity_type: BravoEntity;
  entity_id: string;
  alternative_entity_id?: string;
  source?: string;
  $groups: {
    [key in BravoEntity]?: string;
  };
};

type ListingPageViewedEvent = {
  name: 'listing_page_viewed';
  entity_type: 'campaign' | 'line_item' | 'platform_buy';
  source?: string;
};

type EntityCreatedEvent = {
  name: 'entity_created';
  entity_type: 'line_item' | 'media_plan' | 'platform_buy';
  entity_id: string;
  creation_method: 'new' | 'duplication';
  $groups: {
    [key in BravoEntity]?: string;
  };
};

type PublishStartedEvent = {
  name: 'publish_started';
  entity_type: BravoEntity;
  entity_id: string;
  change_count: number;
  entity_count: number;
  $groups: {
    [key in BravoEntity]?: string;
  };
};

type PublishCompletedEvent = {
  name: 'publish_completed';
  entity_type: BravoEntity;
  entity_id: string;
  change_count: number;
  entity_count: number;
  $groups: {
    [key in BravoEntity]?: string;
  };
};

type PublishEntityEvent = {
  name: 'entity_published';
  entity_type: 'line_item' | 'media_plan' | 'platform_buy';
  entity_id: string;
  change_type: 'new' | 'update' | 'delete';
  change_count: number;
  $groups: {
    [key in BravoEntity]?: string;
  };
};

type BravoEvent =
  | DetailPageViewedEvent
  | ListingPageViewedEvent
  | EntityCreatedEvent
  | PublishStartedEvent
  | PublishCompletedEvent
  | PublishEntityEvent;

export function useCaptureCampaignView(): void {
  const captureEvent = useCaptureEvent();
  const { campaign } = useCampaignContext();
  useEffect(() => {
    captureEvent({
      name: 'detail_page_viewed',
      entity_type: 'campaign',
      entity_id: campaign.campaign_id,
      alternative_entity_id: campaign.campaign_number,
      $groups: { account: campaign.account_id, campaign: campaign.campaign_id }
    });
  }, [campaign, captureEvent]);
}

export function useCaptureLineItemView(lineItem?: LineItemTrpcResponse): void {
  const captureEvent = useCaptureEvent();
  const { campaign } = useCampaignContext();
  const lineItemId = lineItem?.id;
  useEffect(() => {
    if (!lineItemId) {
      return;
    }
    captureEvent({
      name: 'detail_page_viewed',
      entity_type: 'line_item',
      entity_id: lineItemId,
      $groups: {
        account: campaign.account_id,
        campaign: campaign.campaign_id,
        line_item: lineItemId
      }
    });
  }, [campaign, lineItemId, captureEvent]);
}

export function useCaptureMediaPlanView(mediaPlan?: MediaPlan): void {
  const captureEvent = useCaptureEvent();
  const { campaign } = useCampaignContext();
  const { lineItem } = useLineItemContext();
  const mediaPlanId = mediaPlan?.id;
  const mediaBuyId = mediaPlan?.media_buy?.id;
  useEffect(() => {
    if (!mediaPlanId) {
      return;
    }
    captureEvent({
      name: 'detail_page_viewed',
      entity_type: 'media_plan',
      entity_id: mediaPlanId,
      $groups: {
        account: campaign.account_id,
        campaign: campaign.campaign_id,
        line_item: lineItem.id,
        media_plan: mediaPlanId,
        ...(mediaBuyId ? { platform_buy: mediaBuyId } : {})
      }
    });
  }, [captureEvent, campaign, lineItem.id, mediaPlanId, mediaBuyId]);
}

export function useCaptureLineItemCreate(): (
  lineItemId: string,
  creationMethod: 'new' | 'duplication'
) => void {
  const captureEvent = useCaptureEvent();
  const { campaign } = useCampaignContext();
  return useCallback(
    (lineItemId: string, creationMethod: 'new' | 'duplication') => {
      captureEvent({
        name: 'entity_created',
        entity_type: 'line_item',
        entity_id: lineItemId,
        creation_method: creationMethod,
        $groups: {
          account: campaign.account_id,
          campaign: campaign.campaign_id,
          line_item: lineItemId
        }
      });
    },
    [campaign, captureEvent]
  );
}

export function useCaptureMediaPlanCreate(): (
  mediaPlan: { id: string; mediaBuyId?: string },
  lineItem: LineItemTrpcResponse
) => void {
  const captureEvent = useCaptureEvent();
  const { campaign } = useCampaignContext();
  return useCallback(
    (mediaPlan: { id: string; mediaBuyId?: string }, lineItem: LineItemTrpcResponse) => {
      captureEvent({
        name: 'entity_created',
        entity_type: 'media_plan',
        entity_id: mediaPlan.id,
        creation_method: 'new',
        $groups: {
          account: campaign.account_id,
          campaign: campaign.campaign_id,
          line_item: lineItem.id,
          media_plan: mediaPlan.id,
          ...(mediaPlan.mediaBuyId ? { platform_buy: mediaPlan.mediaBuyId } : {})
        }
      });
    },
    [campaign, captureEvent]
  );
}

export function useCaptureListingPageView(entityType: 'campaign' | 'line_item' | 'platform_buy') {
  const captureEvent = useCaptureEvent();
  useEffect(() => {
    captureEvent({ name: 'listing_page_viewed', entity_type: entityType });
  }, [captureEvent, entityType]);
}

function useCaptureEvent() {
  const posthog = usePostHog();
  return useMemo(() => {
    return (event: BravoEvent) => {
      const { name, ...properties } = event;
      identifyGroups(posthog, event);
      posthog.capture(name, properties);
    };
  }, [posthog]);
}

function identifyGroups(posthog: PostHog, event: BravoEvent) {
  if ('$groups' in event) {
    const { account, campaign, line_item, platform_buy, media_plan } = event.$groups;
    if (account) {
      identifyGroup(posthog, 'account', account);
    }
    if (campaign) {
      identifyGroup(posthog, 'campaign', campaign);
    }
    if (line_item) {
      identifyGroup(posthog, 'line_item', line_item);
    }
    if (platform_buy) {
      identifyGroup(posthog, 'platform_buy', platform_buy);
    }
    if (media_plan) {
      identifyGroup(posthog, 'media_plan', media_plan);
    }
  }
}

function identifyGroup(posthog: PostHog, type: BravoEntity, value: string) {
  posthog.capture('$groupidentify', {
    $group_type: type,
    $group_key: value,
    $group_set: { value }
  });
}
